import React, { useState, useEffect } from 'react';
import { LandingAuthProvider } from '../contexts/LandingAuthContext';
import { LandingAppProvider } from '../contexts/LandingAppContext';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';
import { BookOpen, Mic, Brain, GraduationCap, Zap, Trophy, Users, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import NewsletterSignup from '../components/NewsletterSignup';
import RetentionPromiseSection from '../components/RetentionPromiseSection';

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPageV3 = () => {
  const [stats, setStats] = useState({});
  const { user } = useAuth() || {};
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/api/public/stats`);
        setStats(await res.json());
      } catch { /* silent */ }
    };
    fetchStats();
  }, []);

  return (
    <LandingAuthProvider>
      <LandingAppProvider>
        <div className="min-h-screen" style={{ backgroundColor: 'var(--isp-page)' }} data-testid="landing-page">
          <Navbar />

          {/* ─── Hero ─── */}
          <section className="relative pt-28 pb-16 px-4 sm:px-6 lg:px-12 overflow-hidden" data-testid="hero-section">
            <div className="max-w-6xl mx-auto relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                {/* Left: Copy */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                  <div className="inline-flex items-center gap-2 bg-[#002FA7]/8 border border-[#002FA7]/15 rounded-full px-4 py-1.5 mb-6">
                    <span className="w-2 h-2 rounded-full bg-[#34C759] animate-pulse" />
                    <span className="text-xs font-semibold text-[#002FA7] tracking-wide uppercase">The daily ritual for serious students</span>
                  </div>

                  <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-black text-[#0A0A0A] leading-[1.05] tracking-tighter mb-6" style={{ fontFamily: 'var(--sf-display)' }} data-testid="hero-headline">
                    Record your lecture.
                    <br />
                    <span className="text-[#002FA7]">We do the rest</span>
                    <span className="text-[#0A0A0A]">.</span>
                    <br />
                    <span className="text-[#A1A1AA]">Every day. 10 minutes.</span>
                  </h1>

                  <p className="text-lg text-[#52525B] leading-relaxed mb-8 max-w-lg" style={{ fontFamily: 'var(--sf-text)' }}>
                    Hit record in class. By the time you sit down to study, your lecture is already a <strong className="text-[#0A0A0A]">summary, a deck of flashcards and tomorrow's 10-minute plan</strong> — so showing up is the only thing you have to remember.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={() => navigate(user ? '/app' : '/login')} className="px-8 py-4 bg-[#002FA7] text-white font-semibold text-base tracking-tight hover:bg-[#00227A] transition-colors" style={{ fontFamily: 'var(--sf-text)' }} data-testid="hero-cta-button">
                      {user ? 'Go to Dashboard' : 'Start your 7-day habit · Free'}
                    </button>
                    <Link to="/mobile" className="px-8 py-4 border-2 border-[#0A0A0A] text-[#0A0A0A] font-semibold text-base tracking-tight text-center hover:bg-[#F5F5F7] transition-colors inline-flex items-center justify-center gap-2" style={{ fontFamily: 'var(--sf-text)' }} data-testid="hero-mobile-waitlist-btn">
                      Join the mobile waitlist
                    </Link>
                  </div>

                  {/* Google sign-in CTA — visible only to anonymous visitors */}
                  {!user && (
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/api/auth/google/redirect`);
                          const data = await res.json();
                          if (data?.authorization_url) window.location.href = data.authorization_url;
                        } catch {}
                      }}
                      className="mt-3 inline-flex items-center gap-3 px-6 py-3 bg-white border border-[#E4E4E7] hover:border-[#0A0A0A] text-[#0A0A0A] font-semibold text-sm transition-colors group"
                      style={{ fontFamily: 'var(--sf-text)' }}
                      data-testid="hero-google-cta"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span>Continue with Google</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] ml-1">No password</span>
                    </button>
                  )}

                  {/* Trust badges — reframed around the daily-ritual moat (Flame=streaks, Brain=memory across semesters, Trophy=cohort accountability). Replaces the generic "AI features" trio. */}
                  <div className="flex items-center gap-6 mt-8 text-xs text-[#A1A1AA]" style={{ fontFamily: 'var(--sf-text)' }}>
                    <span className="flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Daily streak engine</span>
                    <span className="flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> Remembers every lecture</span>
                    <span className="flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Study with friends</span>
                  </div>
                </motion.div>

                {/* Right: Product visual */}
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.5 }} className="relative">
                  <div className="relative bg-[var(--isp-page)] border border-[#E4E4E7] shadow-2xl shadow-black/5 p-1">
                    <img
                      src="https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?w=800&h=500&fit=crop"
                      alt="Student studying with iStudyPro"
                      className="w-full aspect-[4/3] object-cover"
                      loading="eager"
                    />
                    {/* Floating stats card */}
                    <div className="absolute -bottom-4 -left-4 bg-[var(--isp-page)] border border-[#E4E4E7] shadow-lg p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center"><Zap className="w-5 h-5 text-[#002FA7]" /></div>
                      <div>
                        <p className="text-xs text-[#A1A1AA]" style={{ fontFamily: 'var(--sf-text)' }}>Flashcards Generated</p>
                        <p className="text-lg font-bold text-[#0A0A0A]">{stats.lectures ? stats.lectures * 12 : '50K'}+</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* ─── Featured Creator of the Week — admin pick (renders nothing if none) ─── */}
          {/* Hidden on the marketing page for a focused, F500-concise experience.
              Logged-in users discover content via /feed; landing visitors see the
              promise pitch instead of inventory. */}
          {/* <FeaturedCreatorCard /> */}

          {/* ─── Retention Promise — the "5-year hook" pitch ─── */}
          <RetentionPromiseSection />

          {/* ─── Features Bento Grid ─── */}
          <section id="features" className="py-20 px-4 sm:px-6 lg:px-12 bg-[#F5F5F7]" data-testid="features-section">
            <div className="max-w-6xl mx-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#002FA7] mb-3" style={{ fontFamily: 'var(--sf-text)' }}>How It Works</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] tracking-tight mb-12" style={{ fontFamily: 'var(--sf-display)' }}>
                Everything you need to ace your exams
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E4E4E7]">
                {[
                  { icon: Mic, title: "From lecture to study deck", desc: "Hit record once. Walk out with a clean transcript, a punchy summary, and the flashcards you'd have made yourself — only you didn't have to.", color: "#002FA7" },
                  { icon: BookOpen, title: "A deck that gets smarter", desc: "Today's mistakes become tomorrow's review. You only ever practice what you're about to forget — never the stuff you already know.", color: "#FF3B30" },
                  { icon: Brain, title: "A tutor that knows you", desc: "Tell it your major and your finals date once. From then on, every answer is shaped around your course, your gaps, your deadlines.", color: "#34C759" },
                  { icon: GraduationCap, title: "Four years that compound", desc: "Every recording, every card, every review feeds one growing study transcript. By year four, you can prove exactly how much you remember — and where to focus next.", color: "#FF9500" },
                  { icon: Trophy, title: "Practice the way you'll be tested", desc: "Timed quizzes built from your own lectures, scored by topic so you walk into the exam hall knowing exactly where you stand.", color: "#AF52DE" },
                  { icon: Users, title: "Show up for someone else", desc: "Pair with a friend on a 14-day pact and join live focus rooms with classmates. The hardest part of studying isn't the work — it's the showing up.", color: "#007AFF" },
                ].map(({ icon: Icon, title, desc, color }, i) => (
                  <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="bg-[var(--isp-page)] p-8 lg:p-10 hover:-translate-y-1 transition-transform duration-200" data-testid={`feature-${i}`}>
                    <div className="w-10 h-10 flex items-center justify-center mb-4" style={{ backgroundColor: `${color}10` }}>
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <h3 className="text-base font-semibold text-[#0A0A0A] mb-2 tracking-tight" style={{ fontFamily: 'var(--sf-display)' }}>{title}</h3>
                    <p className="text-sm text-[#52525B] leading-relaxed" style={{ fontFamily: 'var(--sf-text)' }}>{desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Discovery feed, search bar, trending carousel, creator spotlight
              and right-rail leaderboard intentionally hidden on the marketing page
              for a focused, F500-concise experience. Logged-in users find that
              content via /feed and the in-app sidebar. */}

          {/* ─── Student Testimonials ─── */}
          <TestimonialsCarousel />

          {/* ─── Newsletter Signup ─── */}
          <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
            <NewsletterSignup variant="card" source="landing" />
          </section>

          <Footer />
        </div>
      </LandingAppProvider>
    </LandingAuthProvider>
  );
};

export default LandingPageV3;
